import { Injectable } from '@angular/core';

import { Menu } from 'app/shared/models/control/menu';
import { PagesMenu } from 'app/pages/pages.menu';
import { MenuNavigationService } from 'app/shared/services/menu-navigation/menu-navigation.service';
import { normalizeAppUrl } from 'app/shared/utils/app-url.util';

/**
 * Identifies a menu node by its whole chain from the rail module down, e.g. '47/53/57'.
 *
 * Node ids are NOT unique in PagesMenu - 326 nodes share 204 ids, and the collisions happen
 * inside a single module: id 57 is both "Nursing Management > Nursing Worklist" (a screen) and
 * "Pharmacy Management > Management" (a section). Keying any of this on the bare id makes those
 * two the same node, so opening the worklist marks the Pharmacy section as an open screen,
 * highlights it as the active one, and expands the wrong branch. The chain is unique because
 * siblings are.
 */
export type MenuKey = string;

/** Which PagesMenu nodes the currently-open desktop windows correspond to. */
export interface OpenScreens {
    /** Nodes whose screen is open in some window (minimized ones included - still open). */
    openKeys: Set<MenuKey>;
    /**
     * Branch nodes with at least one open descendant - every section on the way down to an open
     * screen. The sidebar expands exactly these, so each open screen is visible in the list
     * rather than buried in a collapsed section.
     */
    containerKeys: Set<MenuKey>;
    /** The node shown in the front-most window, i.e. the screen the user is actually looking at. */
    activeKey: MenuKey | null;
}

export const NO_OPEN_SCREENS: OpenScreens = {
    openKeys: new Set<MenuKey>(),
    containerKeys: new Set<MenuKey>(),
    activeKey: null,
};

/** Every proper prefix of a key, i.e. the keys of the node's ancestor sections. */
export function ancestorKeysOf(key: MenuKey): MenuKey[] {
    const parts = key.split('/');
    return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
}

/**
 * Resolves desktop-window urls back to the PagesMenu nodes that opened them, so the sidebar can
 * show which screens are open.
 *
 * The window manager is the source of truth here rather than the shell's own click handling: a
 * screen can be opened from the flyout, a pinned favorite, a search result, the Home grid or a
 * direct url, and it stays open until its window is closed. Tracking clicks instead (as
 * AppShellComponent.activeScreenId did) only ever knew about the first of those, and forgot even
 * that as soon as the user switched rail modules.
 *
 * The url -> node index is built once; PagesMenu is a static import that never changes at runtime.
 */
@Injectable({ providedIn: 'root' })
export class OpenScreensService {

    /** Screen url -> the key of the node that opens it. */
    private readonly index = new Map<string, MenuKey>();

    constructor(private readonly navigation: MenuNavigationService) {
        this.walk(PagesMenu, '');
    }

    private walk(nodes: Menu[], parentKey: MenuKey): void {
        for (const node of nodes) {
            const key = parentKey ? `${parentKey}/${node.id}` : `${node.id}`;
            const url = this.navigation.screenUrl(node);
            // First writer wins: a url reachable from two menu nodes belongs to the one the
            // sidebar shows first, rather than flickering between them.
            if (url && !this.index.has(url)) {
                this.index.set(url, key);
            }
            if (node.children?.length) {
                this.walk(node.children, key);
            }
        }
    }

    /**
     * `openUrls` are the normalized urls of every open window, `activeUrl` that of the front-most
     * one. Urls with no matching node (parameterized screens drilled into from another screen)
     * simply contribute nothing - there is no sidebar row for them to light up.
     */
    resolve(openUrls: string[], activeUrl: string | null): OpenScreens {
        const openKeys = new Set<MenuKey>();
        const containerKeys = new Set<MenuKey>();
        for (const url of openUrls) {
            const key = this.index.get(url);
            if (!key) {
                continue;
            }
            openKeys.add(key);
            ancestorKeysOf(key).forEach(ancestor => containerKeys.add(ancestor));
        }
        const activeKey = (activeUrl ? this.index.get(activeUrl) : undefined) || null;
        return { openKeys, containerKeys, activeKey };
    }

    /**
     * Key of the node that opens `url`: an exact match, else the node whose own url is the
     * longest prefix of it.
     *
     * The prefix fallback is what keeps a drilled-into screen anchored. Nothing in PagesMenu
     * carries route params, so a record opened from a list
     * (`/pages/patientAccounting/billing/transactions/1596058/139791/2`) matches no node exactly;
     * without the fallback the sidebar would lose its place the moment a list opens a record.
     * The `+ '/'` guard keeps it to real descendants - `/pages/x/userManagementLog` is not a
     * child of `/pages/x/userManagement`. Query string and fragment are dropped: they narrow what
     * a screen shows, they don't make it a different menu node.
     */
    keyForUrl(url: string): MenuKey | null {
        const target = normalizeAppUrl(url).split('?')[0].split('#')[0];
        if (!target) {
            return null;
        }
        const exact = this.index.get(target);
        if (exact) {
            return exact;
        }
        let best: MenuKey | null = null;
        let bestLength = 0;
        this.index.forEach((key, candidate) => {
            if (target.startsWith(candidate + '/') && candidate.length > bestLength) {
                best = key;
                bestLength = candidate.length;
            }
        });
        return best;
    }

    /**
     * The single screen a url puts on screen, as an OpenScreens. Screens route one at a time into
     * the primary outlet here, so the screen that is open and the screen being looked at are the
     * same node, and its ancestors are the sections the sidebar has to expand to show it. A url
     * behind no menu node opens nothing.
     */
    resolveUrl(url: string): OpenScreens {
        const key = this.keyForUrl(url);
        if (!key) {
            return NO_OPEN_SCREENS;
        }
        return {
            openKeys: new Set<MenuKey>([key]),
            containerKeys: new Set<MenuKey>(ancestorKeysOf(key)),
            activeKey: key,
        };
    }
}
